import {
  Container,
  FormControlLabel,
  Grid,
  Stack,
  Skeleton,
  Tooltip,
} from '@mui/material'
import React, {useEffect, useState} from 'react'
import WbButton from '../../../components/common/WbButton'
import WbOutlineButton from '../../../components/common/WbOutlineButton'
import {useNavigate, useParams} from 'react-router-dom'
import paths from '../../../routes/paths'
import WbSwitch from '../../../components/common/WbSwitch'
import SchoolFilter from './SchoolFilter'
import {AUDIENCE, CUSTOMER_TYPE} from '../../../utils/constants'
import PublishButton from '../../../components/screens/surveyForms/PublishButton'
import useCustomerData from '../../../hooks/useCustomerData'
import WbModal from '../../../components/common/WbModal'
import WbAlert from '../../../components/common/WbAlert'
import {participantsApi, surveysApi} from '../../../services'
//import {getMatSites} from '../../../utils/utilities'
import ParticipantFilters from './ParticipantFilters'
import ParticipantCountCard from './ParticipantCountCard'
import {useIdentityAuth, getApiHookParams, normalizeParticipationFilter} from '../../../auth'

export default function SurveyParticipants() {
  const {token} = useParams()
  const navigate = useNavigate()
  const customer = useCustomerData()

  const [participantFilter, setParticipantFilter] = useState(null)
  const [isStaffSurvey, setIsStaffSurvey] = useState(false)
  const [isParentSurvey, setIsParentSurvey] = useState(false)
  const [isStudentSurvey, setIsStudentSurvey] = useState(false)
  const [loading, setLoading] = useState(false)
  const [surveyId, setSurveyId] = useState(null)
  const [isWidgitEnabled, setIsWidgitEnabled] = useState(false)
  const [showLessThan10Alert, setShowLessThan10Alert] = useState(false)
  const [availableFilter, setAvailableFitlers] = useState([])
  const [schools, setSchools] = useState([])
  const [studentCount, setStudentCount] = useState(0)
  const [staffCount, setStaffCount] = useState(0)
  const [parentCount, setParentCount] = useState(0)
  const [loadingStudentCount, setLoadingStudentCount] = useState(false)
  const [loadingStaffCount, setLoadingStaffCount] = useState(false)
  const [loadingParentCount, setLoadingParentCount] = useState(false)
  const [countCards, setCountCards] = useState([])
  const [lockScreen, setLockScreen] = useState(false)
  const [isAnnonymous, setIsAnnonymous] = useState(false)

  const {user, authenticated, authLoading} = useIdentityAuth()
  const apiParams = getApiHookParams()
  const isMat = apiParams.groupId > 0 && apiParams.schoolId === null
  const matId = isMat ? apiParams.groupId : 0
  const schoolId = isMat ? 0 : apiParams.schoolId

  const redirect = path => {
    navigate(path.replace(':token', encodeURIComponent(token)))
  }
  const initdata = async () => {
    setLoading(true)
    let response = await surveysApi.getSurveyParticipationFilters(token)
    // Normalize participation filter from API response
    // Preserve allSchools value from API - don't override it
    const normalizedFilter = normalizeParticipationFilter(
      response?.participationFilter,
      apiParams
    )
    // Ensure allSchools is preserved from API response (don't override)
    if (response?.participationFilter?.allSchools !== undefined) {
      normalizedFilter.allSchools = response.participationFilter.allSchools
    }
    setParticipantFilter(prev => ({...normalizedFilter}))
    setIsWidgitEnabled(response?.isWidgitEnabled)
    setIsParentSurvey(response?.isParentSurvey)
    setIsStaffSurvey(response?.isStaffSurvey)
    setIsStudentSurvey(response?.isStudentSurvey)
    setSurveyId(response?.surveyId)
    setIsAnnonymous(response?.isAnnonymous)
    let avlFitlers = await participantsApi.getParticipantFilters(
      schoolId,
      isMat ? matId : apiParams.group !== null ? apiParams.group?.group_id : 0,
      response.isStaffSurvey,
      response.isStudentSurvey,
      response.isParentSurvey
    )
    setAvailableFitlers(avlFitlers)
    if (matId > 0) {
      var res = await participantsApi.getSchoolsByMatId(apiParams.groupId)
      let sites = apiParams.schoolIds
      if (sites !== null && sites.length > 0) {
        res = res.filter(x => sites.includes(x.id))
      }
      setSchools(res)
    }
    
    if (!isMat) {
      setSchools([{id: schoolId}])
    }
    if (response.isStaffSurvey) {
      setCountCards(prev => [...prev, 'Staff'])
    }
    if (response.isParentSurvey) {
      setCountCards(prev => [...prev, 'Parents'])
    }
    if (response.isStudentSurvey) {
      setCountCards(prev => [...prev, 'Students'])
    }
    updateCounts(
      response?.isParentSurvey,
      response?.isStaffSurvey,
      response?.isStudentSurvey,
      response?.participationFilter
    )
    setLoading(false)
  }

  const saveAndNext = () => {
    if (
      ((isStaffSurvey && staffCount < 10) ||
        (isStudentSurvey && studentCount < 10) ||
        (isParentSurvey && parentCount < 10)) &&
      isAnnonymous
    ) {
      setShowLessThan10Alert(true)
      return
    }
    // Save current participantFilter to DB before navigating
    if (participantFilter) {
      updateInDb(participantFilter)
    }
    redirect(paths.invitesPanel)
  }

  // Helper function to check if two arrays of school IDs are equal
  const areSchoolIdsEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return false
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false
    if (arr1.length !== arr2.length) return false
    const sorted1 = [...arr1].sort((a, b) => a - b)
    const sorted2 = [...arr2].sort((a, b) => a - b)
    return sorted1.every((id, index) => id === sorted2[index])
  }

  const updateInDb = (filters, aud) => {
    // Normalize filters before sending to API
    const normalizedFilters = normalizeParticipationFilter(filters, apiParams)
    
    // Remove schoolIds (plural) - SurveyParticipants only uses schoolId (singular)
    // schoolIds is used in CheckInSurveys, not here
    if (normalizedFilters.schoolIds !== undefined) {
      delete normalizedFilters.schoolIds
    }
    
    // Apply allSchools logic based on hasAllSchoolsAccess
    if (apiParams.hasAllSchoolsAccess === false) {
      // If hasAllSchoolsAccess = false: always send false (regardless of toggle state)
      normalizedFilters.allSchools = false
    } else {
      // If hasAllSchoolsAccess = true: determine toggle state by comparing schoolId with apiParams.schoolIds
      const filterSchoolIds = normalizedFilters.schoolId || []
      if (apiParams.schoolIds && apiParams.schoolIds.length > 0) {
        // If schoolId array matches all apiParams.schoolIds, toggle is ON -> send true
        // Otherwise, toggle is OFF -> send false
        normalizedFilters.allSchools = areSchoolIdsEqual(filterSchoolIds, apiParams.schoolIds)
      } else {
        // If no schoolIds available, default to false
        normalizedFilters.allSchools = false
      }
    }
    let obj = {
      isParentSurvey: aud ? true : aud === 'Student' ? isParentSurvey : false,
      isStaffSurvey: aud ? true : aud === 'Staff' ? isStaffSurvey : false,
      isStudentSurvey: aud ? true : aud === 'Student' ? isStudentSurvey : false,
      participationFilter: normalizedFilters,
      isWidgitEnabled: isWidgitEnabled,
      surveyId: surveyId,
    }
    setLockScreen(true)
    surveysApi.addUpdateParticipantFilter(obj).finally(() => {
      setLockScreen(false)
    })
    updateCounts(isParentSurvey, isStaffSurvey, isStudentSurvey, normalizedFilters, aud)
  }

  const loadCountFromApi = (isParent, isStaff, isStudent, filters) => {
    // Normalize filters before sending to API
    const normalizedFilters = normalizeParticipationFilter(filters, apiParams)
    participantsApi
      .getStaffStudentParentCount({
        isParentSurvey: isParent,
        isStaffSurvey: isStaff,
        isStudentSurvey: isStudent,
        participationFilter: normalizedFilters,
        matId: isMat
          ? matId
          : apiParams.group !== null
            ? apiParams.group?.group_id
            : 0,
        schoolId,
      })
      .then(res => {
        if (res !== null && res !== '') {
          var rr = JSON.parse(res)
          setStudentCount(rr.studentCount)
          setParentCount(rr.parentCount)
          setStaffCount(rr.staffCount)
        }
      })
      .catch()
      .finally(() => {
        setLoadingStaffCount(false)
        setLoadingStudentCount(false)
        setLoadingParentCount(false)
      })
  }

  const updateCounts = (isParent, isStaff, isStudent, filters, aud) => {
    if (filters && filters.filterValues && filters.filterValues.length > 0) {
      var f = [
        ...filters.filterValues.filter(
          x => x.values !== null && x.operator !== null && x.filterType !== null
        ),
      ]

      filters.filterValues = f
    }

    if (isStudent) {
      setLoadingStudentCount(true)
    }
    if (isParent) {
      setLoadingParentCount(true)
    }
    if (isStaff) {
      setLoadingStaffCount(true)
    }

    loadCountFromApi(isParent, isStaff, isStudent, filters)
  }

  useEffect(() => {
    initdata()
  }, [])

  // const publish = () => {
  //   setShowLessThanTenAlertOnPublish(true)
  // }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <div className="body-container participants">
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </div>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      {showLessThan10Alert && (
        <WbModal
          title="Less than 10 participants in an audience for an anonymous survey"
          onClose={() => setShowLessThan10Alert(false)}
          content={
            <>
              <p>
                You have less than 10 participants in an audience for an
                anonymous survey. Do you want to make it none anonimous and
                publish it?
              </p>
              <div style={{textAlign: 'right'}}>
                <WbOutlineButton
                  CustomOutlineButtonText="No"
                  onClick={() => setShowLessThan10Alert(false)}
                ></WbOutlineButton>{' '}
                <WbButton
                  CustomButtonText={'Yes'}
                  onClick={async () => {
                    setShowLessThan10Alert(false)
                    surveysApi.UpdateSurveyAnnonymise(token).then().catch()
                    WbAlert({
                      message: 'Survey published successfully',
                      type: 'success',
                    })
                    redirect(paths.invitesPanel)
                  }}
                />
              </div>
            </>
          }
        />
      )}
      <div className="body-container participants">
        <Grid container>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <h1>Participants & Accessibility</h1>
          </Grid>

          <Grid item lg={12} md={12} sm={12} xs={12} mt={2}>
            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <WbSwitch
                    label={'All Participants'}
                    checked={participantFilter?.all}
                    onChange={e => {
                      // Use apiParams.schoolIds to ensure only accessible schools are included
                      const accessibleSchoolIds = apiParams.schoolIds && apiParams.schoolIds.length > 0
                        ? schools.map(x => x.id).filter(id => apiParams.schoolIds.includes(id))
                        : [...schools.map(x => x.id)]
                      let val = {
                        ...participantFilter,
                        all: e.target.checked,
                        schoolId: accessibleSchoolIds,
                      }
                      // Normalize before setting state
                      const normalizedVal = normalizeParticipationFilter(val, apiParams)
                      setParticipantFilter(normalizedVal)
                      updateInDb(normalizedVal)
                    }}
                  />
                }
                label={'All Participants'}
              />

              <FormControlLabel
                control={
                  <WbSwitch
                    checked={isWidgitEnabled}
                    onChange={e => {
                      setIsWidgitEnabled(e.target.checked)
                      // Normalize participation filter before sending
                      const normalizedFilter = normalizeParticipationFilter(participantFilter, apiParams)
                      let obj = {
                        isParentSurvey: isParentSurvey,
                        isStaffSurvey: isStaffSurvey,
                        isStudentSurvey: isStudentSurvey,
                        participationFilter: normalizedFilter,
                        isWidgitEnabled: e.target.checked,
                        surveyId: surveyId,
                      }
                      surveysApi.addUpdateParticipantFilter(obj)
                    }}
                  />
                }
                label={
                  <Tooltip title="Enabling Widgit will allow participants to see Widgit symbols when they hover over any word or phrase in a survey">
                    <span>Enable Widgit for all participants</span>
                  </Tooltip>
                }
              />
            </Stack>

          </Grid>
          
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <div className="participant-filter-box">
              {!participantFilter?.all && (
                <Grid item lg={12} md={12} sm={12} xs={12} mt={2} mb={2}>
                  <h2>Filter Participants</h2>
                </Grid>
              )}
              <SchoolFilter
                participantFilter={participantFilter}
                setParticipantFilter={setParticipantFilter}
                schools={schools}
                setSchools={setSchools}
                updateInDb={updateInDb}
              />
            </div>
          </Grid>
          {!participantFilter?.all && (isStudentSurvey || isParentSurvey) && (
            <ParticipantFilters
              participantFilter={participantFilter}
              setParticipantFilter={setParticipantFilter}
              audience={'Student'}
              updateInDb={updateInDb}
              availableFilter={[
                ...availableFilter.filter(x => x.audience === 'Student'),
              ]}
            />
          )}
          {!participantFilter?.all && isStaffSurvey && (
            <ParticipantFilters
              participantFilter={participantFilter}
              setParticipantFilter={setParticipantFilter}
              audience={'Staff'}
              updateInDb={updateInDb}
              availableFilter={[
                ...availableFilter.filter(x => x.audience === 'Staff'),
              ]}
            />
          )}
        </Grid>
        <Grid container spacing={2} mt={1} mb={3}>
          {countCards.length > 0 &&
            countCards.map((type, idx) => (
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <ParticipantCountCard
                  key={idx}
                  type={type}
                  studentCount={studentCount}
                  staffCount={staffCount}
                  parentCount={parentCount}
                  loadingStudentCount={loadingStudentCount}
                  loadingStaffCount={loadingStaffCount}
                  loadingParentCount={loadingParentCount}
                  participantFilter={{
                    isParentSurvey,
                    isStaffSurvey,
                    isStudentSurvey,
                    participationFilter: participantFilter,
                    matId: isMat
                      ? matId
                      : apiParams.group !== null
                        ? apiParams.group?.group_id
                        : 0,
                    schoolId: schoolId,
                  }}
                />
              </Grid>
            ))}
        </Grid>
        {lockScreen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              padding: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              textAlign: 'center',
              height: '100vh',
              zIndex: 9999, // Ensure it's on top
            }}
          ></div>
        )}
        <Stack direction="row" justifyContent="space-between">
          <WbOutlineButton
            CustomOutlineButtonText={'Previous'}
            onClick={e => redirect(paths.questionPanel)}
          />
          <div>
            <Stack direction="row" spacing={1}>
              <WbOutlineButton
                CustomOutlineButtonText={'Cancel'}
                onClick={e => redirect(paths.dashboard)}
              />
              <WbButton
                CustomButtonText={'Save and Next'}
                onClick={saveAndNext}
              />
              {/*  <PublishButton
                      survey={survey}
                      onSave={publish}
                    ></PublishButton> */}
            </Stack>
          </div>
        </Stack>
      </div>
    </Container>
  )
}
